import * as React from 'react'
import { styled, css } from 'next-yak'

import { useClientSide } from '@owl-nest/hooks'
import * as plume from '@ulule/owl-kit-components/next'
import * as api from '@owl-nest/api-client/latest'

import { useConfigurationContext } from '../../context/configuration/configurationContext'
import { useURLContext } from '../../context/urls/urlContext'
import { getUrlWithUTMs } from '../../utils/urls'

import { ChannelList } from '../Channel'
import { MobileNavigationDrawer } from '../drawers/MobileNavigationDrawer'
import { Toggle } from '../Toggle'
import { UluleLogo } from '../logos/UluleLogo'

import * as S from '../style'

const ICON_CONTAINER_CLASSNAME = 'list-icon-hover'

type Items = {
  href: string
  target?: string
  icon?: React.ReactNode
  onClick?: () => void
  title: string
}

type LeftProps = {
  siteIdentifier: string
  subnavConfig?: {
    mobileOnlySection: React.ReactNode
    title?: React.ReactNode
    withDiscoverLink?: boolean
    withSeparator?: boolean
  }
  onClose?: () => void
  isDrawerOpen: boolean
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
  lang: api.Lang
}

export function Left({
  siteIdentifier,
  subnavConfig,
  onClose,
  isDrawerOpen = false,
  setIsDrawerOpen,
  lang,
}: LeftProps): React.ReactElement<LeftProps> {
  const isClientSide = useClientSide()
  const { translations } = useConfigurationContext()
  const urlContext = useURLContext()

  const hasSubnav = subnavConfig?.mobileOnlySection !== undefined
  const kind = hasSubnav ? 'secondary' : 'primary'

  const menuItems = React.useMemo(() => getMenuItems(), [urlContext])

  const breakpointType = S.INTERMEDIATE_BREAKPOINT_TYPE
  return (
    <>
      <S.DesktopOnlyNav $breakpoint={breakpointType}>
        <LogoLink href={getUrlWithUTMs(urlContext.home, siteIdentifier, 'ulule', 'logo')}>
          <UluleLogo />
        </LogoLink>
        {menuItems.map((item) => {
          if (item.items !== undefined) {
            return (
              <plume.Menu forceMount={!isClientSide} key={item.title} title={item.title}>
                {ItemsLineDropdown(item.items)}
              </plume.Menu>
            )
          } else {
            return (
              <plume.styles.copy.S.regular key={item.title}>
                <S.MainNavLinkItem $breakpoint={breakpointType} href={item.link}>
                  {item.title}
                </S.MainNavLinkItem>
              </plume.styles.copy.S.regular>
            )
          }
        })}
      </S.DesktopOnlyNav>
      <MobileOnly $breakpoint={breakpointType}>
        <MobileNavigationDrawer
          setOpen={(open) => {
            setIsDrawerOpen(open)

            if (!open && onClose) {
              onClose()
            }
          }}
          open={isDrawerOpen}
          title={subnavConfig?.title}
        >
          {subnavConfig?.mobileOnlySection}
          {hasSubnav && (
            <SeparatorWrapper>
              {subnavConfig.withSeparator && <hr />}
              <plume.styles.heading.XXXXS>Ulule.com</plume.styles.heading.XXXXS>
            </SeparatorWrapper>
          )}
          <S.CommonMobileEntries>
            {menuItems.map((item) => {
              if (item.items !== undefined) {
                return (
                  <Toggle key={item.title} kind={kind} title={item.title}>
                    {ItemsLineToggle(item.items, kind)}
                  </Toggle>
                )
              } else {
                return (
                  <S.LinkItem $breakpoint={breakpointType} key={item.title} kind={kind} href={item.link}>
                    {item.title}
                  </S.LinkItem>
                )
              }
            })}
            {subnavConfig?.withDiscoverLink && (
              <S.LinkItem $breakpoint={breakpointType} kind={kind} href={urlContext.discoverLandingPage}>
                <plume.glyphs.stroke.Menu size={20} />
                {translations.discoverUlule as any}
              </S.LinkItem>
            )}
          </S.CommonMobileEntries>
        </MobileNavigationDrawer>
        <S.Overlay $visible={isDrawerOpen}>{isDrawerOpen && <S.LockScroll />}</S.Overlay>
      </MobileOnly>
    </>
  )

  function getMenuItems() {
    if (lang !== 'fr') {
      return [
        {
          title: (translations.navigationItems as Record<string, string>).startProject,
          link: getUrlWithUTMs(urlContext.proposal, siteIdentifier, 'ulule', 'header'),
        },
        {
          title: (translations.navigationItems as Record<string, string>).seeAllProjects,
          link: getUrlWithUTMs(urlContext.discover, siteIdentifier, 'ulule', 'header'),
        },
      ]
    }

    return [
      {
        title: 'Lancer une collecte',
        link: getUrlWithUTMs(urlContext.proposal, siteIdentifier, 'ulule', 'header'),
      },
      {
        title: 'Développer son projet',
        items: [
          [
            {
              title: 'Rejoindre le camp de base',
              href: getUrlWithUTMs('https://campdebase.ulule.com', siteIdentifier, 'header', 'sous_header'),
              target: '_blank',
            },
            {
              title: 'Nos formations pour créateurs et entrepreneurs',
              href: getUrlWithUTMs('https://formation.ulule.com', siteIdentifier, 'header', 'sous_header'),
              target: '_blank',
            },
            {
              title: 'Nos bilans de compétences',
              href: getUrlWithUTMs(
                'https://formation.ulule.com/formations/bilan-de-competences',
                siteIdentifier,
                'header',
                'sous_header',
              ),
              target: '_blank',
            },
          ],
        ],
      },
      {
        title: 'Partenariats',
        items: [
          [
            {
              title: 'Les appels à projets en cours',
              href: getUrlWithUTMs(
                'https://fr.ulule.com/pages/appel-a-projet/?ref=header',
                siteIdentifier,
                'header',
                'sous_header',
              ),
            },
            {
              title: 'Organiser un appel à projets',
              href: getUrlWithUTMs(
                'https://partenaires.ulule.com/organiser-appel-projets',
                siteIdentifier,
                'header',
                'sous_header',
              ),
              target: '_blank',
            },
            {
              title: 'Communiquer auprès des makers',
              href: getUrlWithUTMs(
                'https://partenaires.ulule.com/nous-contacter',
                siteIdentifier,
                'header',
                'sous_header',
              ),
              target: '_blank',
            },
          ],
        ],
      },
      {
        title: 'À propos',
        items: [
          [
            {
              title: 'Découvrir Ulule',
              href: getUrlWithUTMs(
                'https://fr.ulule.com/pages/discover/?ref=header',
                siteIdentifier,
                'header',
                'sous_header',
              ),
            },
            {
              title: 'Explorer les projets en cours de financement',
              href: getUrlWithUTMs(
                'https://fr.ulule.com/discover/?ref=header',
                siteIdentifier,
                'header',
                'sous_header',
              ),
            },
            {
              title: 'Support, contact et questions fréquentes',
              href: getUrlWithUTMs('https://support.ulule.com/hc/fr-fr', siteIdentifier, 'header', 'sous_header'),
              target: '_blank',
            },
          ],
        ],
      },
    ]
  }
}

function ItemsLineDropdown(listItems: Array<Items[]>): React.ReactElement<any> {
  return (
    <ItemsLineDropdownWrapper>
      {listItems.map((items, index) => (
        <React.Fragment key={index}>
          {items.map((item) => (
            <li key={item.title} onClick={item?.onClick} className={ICON_CONTAINER_CLASSNAME}>
              <plume.Link href={item?.href} target={item?.target}>
                {item.icon}
                <plume.styles.copy.S.semiBold>{item.title}</plume.styles.copy.S.semiBold>
                <plume.glyphs.stroke.ArrowRight size={16} />
              </plume.Link>
            </li>
          ))}
          {index !== listItems.length - 1 && (
            <li>
              <hr />
            </li>
          )}
        </React.Fragment>
      ))}
    </ItemsLineDropdownWrapper>
  )
}

function ItemsLineToggle(listItems: Array<Items[]>, kind: 'secondary' | 'primary') {
  return (
    <ItemsLineToggleWrapper $kind={kind}>
      {listItems.flatMap((items, index) => {
        const list = items.map((item, index) => (
          <li onClick={item?.onClick} key={`${item?.href}-${index}`}>
            <plume.Link kind={kind} href={item?.href}>
              {item.icon}
              <plume.styles.copy.S.semiBold>{item.title}</plume.styles.copy.S.semiBold>
            </plume.Link>
          </li>
        ))
        if (index !== listItems.length - 1) {
          list.push(
            <li key={`hr-${index}`}>
              <hr />
            </li>,
          )
        }
        return list
      })}
    </ItemsLineToggleWrapper>
  )
}

const MobileOnly = styled(S.MobileOnlyNav)`
  ${ChannelList} {
    gap: 10px;
    grid-template-columns: repeat(2, 1fr);
    margin-top: 16px;
  }
`

const LogoLink = styled.a`
  align-items: center;
  color: ${plume.COLORS.TEXT_PRIMARY};
  display: flex;
  height: 100%;
  justify-content: center;
`

const ItemsLineDropdownWrapper = styled.div`
  *:last-child {
    margin-bottom: 0;
  }

  ${plume.glyphs.Icon} {
    overflow: visible;
  }

  li {
    margin-bottom: 16px;
    position: relative;
    padding-right: 50px;

    hr {
      border-color: ${plume.COLORS.PRIMARY_SAND_200};
      width: calc(100% + 50px);
      border: 0px;
      border-bottom: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
    }

    ${plume.Link} {
      align-items: center;
      display: flex;
    }

    ${plume.styles.copy.S.semiBold} {
      margin-left: 16px;
    }

    ${plume.glyphs.stroke.ArrowRight} {
      right: 15px;
      position: absolute;
      top: 3px;
      transition-duration: 300ms;
      transition-property: opacity, right;
      transition-timing-function: cubic-bezier(0.83, 0, 0.17, 1);
      opacity: 0;
    }

    &:hover {
      ${plume.glyphs.colored.Wrapper} {
        background-color: ${plume.COLORS.PRIMARY_BLUE_200};
      }

      ${plume.styles.copy.S.semiBold} {
        color: ${plume.COLORS.PRIMARY_BLUE_700};
      }

      ${plume.glyphs.stroke.ArrowRight} {
        color: ${plume.COLORS.TEXT_LINK_HOVER};
        right: -5px;
        opacity: 1;
      }
    }
  }
`

export const ItemsLineToggleWrapper = styled.div<{ $kind: 'secondary' | 'primary' }>`
  *:last-child {
    margin-bottom: 0;
  }

  ${plume.glyphs.Icon} {
    overflow: visible;
  }

  li {
    margin-bottom: 16px;
    position: relative;

    hr {
      width: 100%;
      border: 0px;
      border-bottom: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
    }

    ${plume.Link} {
      align-items: center;
      display: flex;
    }

    ${plume.styles.copy.S.semiBold} {
      margin-left: 16px;

      ${({ $kind }) => {
        if ($kind === 'secondary') {
          return css`
            color: ${plume.COLORS.TEXT_TERTIARY};
          `
        }
      }}
    }

    &:hover {
      ${plume.styles.copy.S.semiBold} {
        color: ${plume.COLORS.PRIMARY_BLUE_700};
      }
    }
  }
`

const SeparatorWrapper = styled.span`
  ${plume.styles.heading.XXXXS} {
    padding-top: 40px;
    color: ${plume.COLORS.TEXT_PRIMARY};
  }

  hr {
    border: 0px;
    border-bottom: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
    left: 0;
    position: absolute;
    width: 100%;
  }
`
